import type { Avatar, AvatarColor } from '@oefy/core';
import { useState } from 'react';
import { AvatarArt } from './avatar-art';
import { cx } from './styles';

export const avatarBackgrounds: Record<AvatarColor, string> = {
  orange: 'bg-orange-100',
  sky: 'bg-sky-100',
  emerald: 'bg-emerald-100',
  violet: 'bg-violet-100',
  rose: 'bg-rose-100',
  amber: 'bg-amber-100',
};

export const avatarSwatches: Record<AvatarColor, string> = {
  orange: 'bg-orange-300',
  sky: 'bg-sky-300',
  emerald: 'bg-emerald-300',
  violet: 'bg-violet-300',
  rose: 'bg-rose-300',
  amber: 'bg-amber-300',
};

export const avatarColorNames: Record<AvatarColor, string> = {
  orange: 'Oranje',
  sky: 'Blauw',
  emerald: 'Groen',
  violet: 'Paars',
  rose: 'Roze',
  amber: 'Geel',
};

const sizes = {
  xs: 'size-6',
  sm: 'size-8',
  md: 'size-10',
  lg: 'size-14',
  xl: 'size-24',
  '2xl': 'size-28',
};

const initialSizes = {
  xs: 'text-[0.625rem]',
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-lg',
  xl: 'text-3xl',
  '2xl': 'text-4xl',
};

export type AvatarSize = keyof typeof sizes;

function Photo({ src, onFail }: { readonly src: string; readonly onFail: () => void }) {
  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      onError={onFail}
      className="size-full object-cover"
    />
  );
}

interface ChildAvatarProps {
  readonly avatar: Avatar;
  readonly color: AvatarColor;
  readonly photoUrl?: string | null;
  readonly size?: AvatarSize;
  readonly className?: string;
}

export function ChildAvatar({ avatar, color, photoUrl, size = 'md', className }: ChildAvatarProps) {
  const [failed, setFailed] = useState<string | null>(null);
  const showPhoto = photoUrl && failed !== photoUrl;
  return (
    <span
      aria-hidden="true"
      className={cx(
        'inline-flex shrink-0 overflow-hidden rounded-full',
        avatarBackgrounds[color],
        sizes[size],
        className,
      )}
    >
      {showPhoto ? (
        <Photo
          src={photoUrl}
          onFail={() => {
            setFailed(photoUrl);
          }}
        />
      ) : (
        <AvatarArt avatar={avatar} className="size-full" />
      )}
    </span>
  );
}

export function initialsOf(name: string | null, email: string): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  const first = words[0]?.charAt(0) ?? email.charAt(0);
  const last = words.length > 1 ? (words.at(-1)?.charAt(0) ?? '') : '';
  return `${first}${last}`.toUpperCase();
}

interface UserAvatarProps {
  readonly name: string | null;
  readonly email: string;
  readonly photoUrl?: string | null;
  readonly size?: AvatarSize;
  readonly className?: string;
}

export function UserAvatar({ name, email, photoUrl, size = 'md', className }: UserAvatarProps) {
  const [failed, setFailed] = useState<string | null>(null);
  const showPhoto = photoUrl && failed !== photoUrl;
  return (
    <span
      aria-hidden="true"
      className={cx(
        'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-brand-600 font-display font-bold text-white',
        sizes[size],
        initialSizes[size],
        className,
      )}
    >
      {showPhoto ? (
        <Photo
          src={photoUrl}
          onFail={() => {
            setFailed(photoUrl);
          }}
        />
      ) : (
        initialsOf(name, email)
      )}
    </span>
  );
}

export function firstNameOf(name: string | null): string | null {
  return name?.trim().split(/\s+/)[0] ?? null;
}
